import { useState, useCallback, useMemo, useRef } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { insertDiagnosisTeeth } from './dental-diagnosis-service';
import { useAuth } from '@helios/auth-firebase-web';
import {
  diagnosisToothId,
  diffDiagnosisTeeth,
  summarizeDiagnosisTeeth,
  groupTeethByDiagnosis,
  parseFindingTeeth,
  qClinicalFindingsByEncounter,
  qDiagnosesByEncounter,
  qDiagnosisSuggestions,
  mergeDiagnosisSuggestions,
  qDiagnosisSuggestionsByEncounter,
  qDiagnosisTeethByEncounter,
  qAppConfig,
  getMedicalHistorySettings,
  qOdontogramChartsByEncounter,
  parseToothStatesMap,
  suggestedTeethFromFindings,
  mergeSuggestedTeeth,
  buildChartSuggestions,
  type ClinicalFindingRow,
  type DiagnosisToothRow,
  type OdontogramChartRow,
  type AppConfigRow,
  type PatientDiagnosisEntryRow,
  type PatientDiagnosisSuggestionRow,
  type VttSuggestionRow,
} from '@helios/data-access';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { ChipListField, type Chip } from './ChipListField';
import { ToothPickerModal } from './ToothPickerModal';
import { HistoryEntryEditor } from './HistoryEntryEditor'; // PRD.316 — shared autosave + voice

/* ------------------------------------------------------------------ */
/*  Diagnosis structured entry (PRD.214 Phase 2 + PRD.217).           */
/*                                                                    */
/*  AI dermatology suggestions are auto-accepted on the client when   */
/*  analysis completes — they land as regular diagnosis chips with    */
/*  an inline "AI Suggested" badge so clinicians can dismiss them     */
/*  with one click (the chip X) if they don't agree.                  */
/* ------------------------------------------------------------------ */

interface Props {
  patientId: string;
  encounterId: string;
  /** When true, hide the internal "Diagnosis" header (used inside guided-note cards). */
  hideHeader?: boolean;
  /**
   * PRD.317 — render the compact tooth-context layout.
   *
   * Off by default so General / Aesthetic keep the familiar chip row exactly as
   * before: tooth selectors must not be forced into non-dental workflows
   * (PRD §8.1). The underlying Diagnosis model is identical either way.
   */
  dentalContext?: boolean;
}

/** Border + padding above a compact field's first line — see ClinicalFindingsSection. */
const FIRST_LINE_OFFSET = 7;

export function EncounterDiagnosesTab({
  patientId,
  encounterId,
  hideHeader,
  dentalContext = false,
}: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const [toothPickerFor, setToothPickerFor] = useState<string | null>(null);
  /**
   * The one UNPERSISTED diagnosis row that "+ Add Diagnosis" (or a suggested
   * tooth chip) opens. It becomes a real record on its first meaningful save,
   * so an abandoned click never leaves a blank diagnosis behind — the same rule
   * Clinical Findings uses.
   */
  const [draft, setDraft] = useState<{ id: string; teeth: string[] } | null>(null);
  /**
   * Metadata for ids handed to an editor but not yet INSERTed, keyed by id so a
   * superseded draft can still promote itself if its debounced save lands after
   * a second draft was opened.
   */
  const pendingDraftsRef = useRef(new Map<string, { teeth: string[] }>());

  // Voice availability reuses the existing clinic capability switch, exactly as
  // Clinical Findings does. It gates the CONTROLS only.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const voiceSettings = useMemo(
    () => getMedicalHistorySettings(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  const { data: entries = [] } = usePowerSyncQuery<PatientDiagnosisEntryRow>(
    qDiagnosesByEncounter(encounterId),
  );

  // PRD.217 — pull suggestions so we can tag each linked entry with an
  // "AI Suggested" badge. Auto-accept means we only care about rows whose
  // status === 'accepted' and have a populated accepted_diagnosis_entry_id.
  const { data: suggestionRowsForBadge = [] } = usePowerSyncQuery<PatientDiagnosisSuggestionRow>(
    qDiagnosisSuggestionsByEncounter(encounterId),
  );

  const aiEntryIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of suggestionRowsForBadge) {
      if (s.status === 'accepted' && s.accepted_diagnosis_entry_id) {
        set.add(s.accepted_diagnosis_entry_id);
      }
    }
    return set;
  }, [suggestionRowsForBadge]);

  const [search, setSearch] = useState('');
  const { data: suggestionRows = [] } = usePowerSyncQuery<VttSuggestionRow>(
    qDiagnosisSuggestions(orgId, search),
  );
  const suggestions = useMemo(() => suggestionRows.map((r) => r.label), [suggestionRows]);

  /**
   * Dental diagnosis suggestions — the SAME catalogue the tooth chart uses,
   * from `@helios/data-access`, not a second copy.
   *
   * Dental ONLY (§10): General and Aesthetic keep their existing behaviour,
   * which is the org's own history via `suggestions` above. Injecting 40 dental
   * labels into a dermatology encounter would be noise.
   *
   * Catalogue first: `patient_diagnosis_entries` is shared across specialties,
   * so org history on a dental encounter is largely non-dental text.
   */
  const dentalSuggestions = useMemo(
    () => (dentalContext ? mergeDiagnosisSuggestions(suggestions, search, 40, true) : []),
    [dentalContext, suggestions, search],
  );

  const chips: Chip[] = useMemo(
    () =>
      entries.map((e) => ({
        id: e.id,
        label: e.label,
        badge: aiEntryIds.has(e.id) ? 'AI Suggested' : undefined,
      })),
    [entries, aiEntryIds],
  );

  /**
   * Rows to render: saved diagnoses plus the in-memory draft.
   *
   * The draft carries the id it will be INSERTed with, so promotion keeps the
   * same key, React keeps the same editor instance, and the clinician's caret
   * and in-flight text survive the transition.
   */
  const rows = useMemo(() => {
    const list = entries.map((e) => ({
      id: e.id,
      label: e.label as string | null,
      badge: aiEntryIds.has(e.id) ? 'AI Suggested' : undefined,
      isDraft: false,
    }));
    if (draft && !entries.some((e) => e.id === draft.id)) {
      list.push({ id: draft.id, label: null, badge: undefined, isDraft: true });
    }
    return list;
  }, [entries, aiEntryIds, draft]);

  // Shared with the chart's Part Conditions modal so both write tooth
  // associations through one set of statements.
  const insertToothRows = useCallback(
    async (diagnosisId: string, teeth: readonly string[]) => {
      if (!db) return;
      await insertDiagnosisTeeth(db, { orgId, patientId, encounterId }, diagnosisId, teeth);
    },
    [db, orgId, patientId, encounterId],
  );

  const handleAdd = useCallback(
    async (label: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_diagnosis_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [crypto.randomUUID(), orgId, null, patientId, encounterId, label, now, now],
      );
    },
    [db, orgId, patientId, encounterId],
  );

  /** Open an unpersisted row, optionally pre-seeded with tooth context. */
  const startDraft = useCallback((teeth: string[]) => {
    const id = crypto.randomUUID();
    pendingDraftsRef.current.set(id, { teeth });
    setDraft({ id, teeth });
  }, []);

  /**
   * Autosave a diagnosis label — the Clinical Findings lifecycle (600 ms
   * debounce, blur flush, unmount flush, identity-captured writes) arrives with
   * `HistoryEntryEditor`; only the write differs.
   *
   * A draft is INSERTed on its first meaningful text, together with any teeth
   * chosen for it. An existing row is UPDATEd.
   *
   * Empty text is a no-op rather than a write: `label` is NOT NULL, so it
   * cannot be blanked, and silently deleting the row on an accidental
   * select-all-delete would destroy clinical data. Removal stays the explicit ×.
   */
  const saveLabel = useCallback(
    async (text: string, diagnosisId: string) => {
      if (!db) return;
      const trimmed = text.trim();
      const now = new Date().toISOString();

      const pending = pendingDraftsRef.current.get(diagnosisId);
      if (pending) {
        if (!trimmed) return;
        await db.execute(
          `INSERT INTO patient_diagnosis_entries
            (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          [diagnosisId, orgId, null, patientId, encounterId, trimmed, now, now],
        );
        if (pending.teeth.length > 0) await insertToothRows(diagnosisId, pending.teeth);
        pendingDraftsRef.current.delete(diagnosisId);
        setDraft((d) => (d?.id === diagnosisId ? null : d));
        return;
      }

      if (!trimmed) return;
      await db.execute(
        `UPDATE patient_diagnosis_entries SET label = ?, updated_at = ? WHERE id = ?`,
        [trimmed, now, diagnosisId],
      );
    },
    [db, orgId, patientId, encounterId, insertToothRows],
  );

  const handleRemove = useCallback(
    async (chip: Chip) => {
      if (!db) return;
      // PRD.317 — clear the association first so removing a diagnosis never
      // strands its tooth rows (there is no cascade on the client views).
      //
      // Guarded: orphaned tooth rows are invisible and harmless, but a
      // diagnosis the clinician cannot delete is not. Before the
      // `patient_diagnosis_teeth` migration has run this statement fails, and
      // an unguarded await would block deletion entirely.
      try {
        await db.execute(`DELETE FROM patient_diagnosis_teeth WHERE diagnosis_entry_id = ?`, [
          chip.id,
        ]);
      } catch (err) {
        console.warn('[diagnosis] could not clear tooth associations', err);
      }
      await db.execute(`DELETE FROM patient_diagnosis_entries WHERE id = ?`, [chip.id]);
    },
    [db],
  );

  /** Remove a row: discard an unpersisted draft, otherwise delete the record. */
  const removeRow = useCallback(
    async (id: string) => {
      if (pendingDraftsRef.current.has(id)) {
        pendingDraftsRef.current.delete(id);
        setDraft((d) => (d?.id === id ? null : d));
        return;
      }
      await handleRemove({ id, label: '' });
    },
    [handleRemove],
  );

  // ── PRD.317 — optional dental tooth context ──
  //
  // Every query below is gated on `dentalContext`, so a General or Aesthetic
  // clinic issues none of them and behaves exactly as it did before.

  const { data: toothRows = [] } = usePowerSyncQuery<DiagnosisToothRow>(
    qDiagnosisTeethByEncounter(dentalContext ? encounterId : ''),
  );
  const teethByDiagnosis = useMemo(() => groupTeethByDiagnosis(toothRows), [toothRows]);

  // Tooth context suggestions come from this encounter's Clinical Findings.
  // TOOTH NUMBERS ONLY — never the finding narrative or its chart condition:
  // a carious lesion on 16 is not a diagnosis (PRD §10.1). Diagnosis works
  // unchanged when PRD.316 is disabled or has no findings; the list is simply
  // empty (PRD §10.3).
  const { data: findingRows = [] } = usePowerSyncQuery<ClinicalFindingRow>(
    qClinicalFindingsByEncounter(dentalContext ? encounterId : ''),
  );
  const findingTeeth = useMemo(
    () => suggestedTeethFromFindings(findingRows.map((f) => parseFindingTeeth(f.teeth))),
    [findingRows],
  );

  // Live chart state, purely so the picker renders the same visual context the
  // Part Chart shows. Read-only: Diagnosis never writes to `odontogram_charts`.
  const { data: chartRows = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByEncounter(dentalContext ? encounterId : ''),
  );
  const toothStates = useMemo(
    () => (chartRows[0] ? parseToothStatesMap(chartRows[0].tooth_states) : {}),
    [chartRows],
  );

  /**
   * Teeth the chart says are worth attention this visit.
   *
   * Derived from the SAME chart-suggestion logic Clinical Findings uses, then
   * reduced to bare tooth numbers: the condition that made a tooth interesting
   * (caries, implant, …) is deliberately discarded, because offering
   * "16 · Caries" here would read as a suggested diagnosis, which PRD §11
   * forbids. The chart makes a tooth easy to pick; it never proposes what is
   * wrong with it.
   */
  const chartTeeth = useMemo(() => {
    const teeth: string[] = [];
    for (const chart of chartRows) {
      for (const s of buildChartSuggestions(chart.id, parseToothStatesMap(chart.tooth_states))) {
        teeth.push(s.tooth);
      }
    }
    return teeth;
  }, [chartRows]);

  // Not filtered against already-diagnosed teeth: one tooth may carry several
  // diagnoses in an encounter (PRD §6.3).
  const suggestedTeeth = useMemo(
    () => mergeSuggestedTeeth(chartTeeth, findingTeeth),
    [chartTeeth, findingTeeth],
  );

  /**
   * Persist a diagnosis's tooth selection as a minimal diff.
   *
   * Only genuinely added/removed teeth produce a write; an unchanged selection
   * queues nothing. Ids are deterministic, so a re-added tooth reuses its id
   * and converges rather than duplicating.
   */
  const saveTeeth = useCallback(
    async (diagnosisId: string, next: string[]) => {
      if (!db) return;
      const current = teethByDiagnosis.get(diagnosisId) ?? [];
      const { added, removed } = diffDiagnosisTeeth(current, next);

      for (const tooth of removed) {
        await db.execute(`DELETE FROM patient_diagnosis_teeth WHERE id = ?`, [
          diagnosisToothId(diagnosisId, tooth),
        ]);
      }
      await insertToothRows(diagnosisId, added);
    },
    [db, teethByDiagnosis, insertToothRows],
  );

  return (
    <div
      style={{
        // Dental mode renders a row list, so it adopts Clinical Findings'
        // padding and row rhythm; the chip layout keeps its original spacing.
        padding: dentalContext ? 'var(--sp-4) var(--sp-5)' : 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: dentalContext ? 'var(--sp-4)' : 'var(--sp-3)',
      }}
    >
      {!hideHeader && (
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Diagnosis
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Working diagnosis for this encounter.
          </p>
        </div>
      )}

      {/* PRD.317 — dental clinics get one compact ROW per diagnosis with its
          tooth context; every other discipline keeps the original chip row.
          Diagnosis remains a catalogue-style tag either way: the label is the
          same free-text value, created and removed the same way. */}
      {dentalContext ? (
        <>
          {/* ── Suggested from Chart ──
              TOOTH CONTEXT ONLY. Tapping a chip starts a diagnosis on that
              tooth; it never fills in a diagnosis. The chart knowing tooth 16
              has caries does NOT make "Dental caries" the diagnosis — that
              judgement stays with the clinician (PRD §11). Nothing is persisted
              until the clinician actually types one. */}
          {suggestedTeeth.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-muted)',
                }}
              >
                Suggested from Chart
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {suggestedTeeth.map((tooth) => (
                  <button
                    key={tooth}
                    onClick={() => void startDraft([tooth])}
                    title={`Start a diagnosis on tooth ${tooth}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-full)',
                      border: `1px dashed ${primary}`,
                      background: 'transparent',
                      color: primary,
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>+</span>
                    {tooth}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* One compact row per diagnosis, matching Clinical Findings: tooth
              context left, autosaving field with dictation centre, remove right. */}
          {rows.map((row) => {
            const teeth = row.isDraft ? (draft?.teeth ?? []) : (teethByDiagnosis.get(row.id) ?? []);
            const summary = summarizeDiagnosisTeeth(teeth);
            return (
              <div
                key={row.id}
                className="pc-finding-row"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--sp-2)',
                  paddingBottom: 'var(--sp-2)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <button
                  className="pc-finding-context"
                  onClick={() => setToothPickerFor(row.id)}
                  title={
                    summary.hiddenCount > 0
                      ? `Teeth: ${summary.fullLabel}`
                      : 'Choose teeth for this diagnosis'
                  }
                  aria-label={
                    summary.all.length > 0
                      ? `Teeth ${summary.fullLabel}. Choose teeth for this diagnosis`
                      : 'No tooth association. Choose teeth for this diagnosis'
                  }
                  style={{
                    padding: 0,
                    paddingTop: FIRST_LINE_OFFSET,
                    border: 'none',
                    background: 'transparent',
                    color: summary.label ? 'var(--text)' : 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                    lineHeight: 'var(--line-height-body)',
                    fontWeight: summary.label ? 600 : 400,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    alignSelf: 'flex-start',
                  }}
                >
                  {summary.label || 'General'}
                </button>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* `autoRowsMax={1}` keeps this a single-line field: Diagnosis
                      gains autosave and dictation, but does NOT become the
                      multi-line narrative editor findings use. */}
                  <HistoryEntryEditor
                    identity={row.id}
                    storedValue={row.label ?? ''}
                    onSave={saveLabel}
                    label="Diagnosis"
                    placeholder="Add diagnosis…"
                    voiceEnabled={voiceSettings.voiceToTextEnabled}
                    autoRowsMax={1}
                    // Additive: the field stays free text with dictation. A
                    // diagnosis outside the catalogue saves exactly as before.
                    suggestions={dentalSuggestions}
                    onSearchChange={setSearch}
                  />
                </div>

                {row.badge && (
                  <span
                    style={{
                      marginTop: FIRST_LINE_OFFSET,
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--bg-surface-2)',
                      color: 'var(--text-muted)',
                      fontSize: 'calc(var(--font-size-xs) * 0.85)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.badge}
                  </span>
                )}

                <button
                  onClick={() => void removeRow(row.id)}
                  aria-label="Remove diagnosis"
                  title="Remove diagnosis"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginTop: FIRST_LINE_OFFSET + 4,
                    padding: 4,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  <NavIcon name="x" />
                </button>
              </div>
            );
          })}

          <div>
            <button
              onClick={() => void startDraft([])}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${primary}`,
                background: 'transparent',
                color: primary,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <NavIcon name="plus" />
              Add Diagnosis
            </button>
          </div>
        </>
      ) : (
        <ChipListField
          chips={chips}
          suggestions={suggestions}
          onSearchChange={setSearch}
          onAdd={handleAdd}
          onRemove={handleRemove}
          subtitle="Add or edit the diagnosis"
          addLabel="Add Diagnosis"
          placeholder="Add or edit diagnosis…"
        />
      )}

      {toothPickerFor && (
        <ToothPickerModal
          open
          initialTeeth={(
            pendingDraftsRef.current.get(toothPickerFor)?.teeth ??
            teethByDiagnosis.get(toothPickerFor) ??
            []
          ).map((tooth: string) => ({ tooth, surfaces: [] }))}
          toothStates={toothStates}
          primary={primary}
          // PRD §7 — teeth only; surface context belongs to the chart and to
          // Clinical Findings, not to a diagnosis.
          showSurfaces={false}
          suggestedTeeth={suggestedTeeth}
          onClose={() => setToothPickerFor(null)}
          onSave={(teeth) => {
            const picked = teeth.map((t) => t.tooth);
            const pending = pendingDraftsRef.current.get(toothPickerFor);
            if (pending) {
              // Held in memory until the diagnosis itself is saved — nothing is
              // written for a tooth selection the clinician abandons.
              pending.teeth = picked;
              setDraft((d) => (d?.id === toothPickerFor ? { ...d, teeth: picked } : d));
            } else {
              void saveTeeth(toothPickerFor, picked);
            }
            setToothPickerFor(null);
          }}
        />
      )}
    </div>
  );
}
